import { useEffect, useRef } from "react"
import { MapPinned } from "lucide-react"
import type { GeoJSONSource } from "maplibre-gl"
import { cn } from "@/lib/utils"

type Coordinates = [number, number]

export type MapFeatureProperties = {
  id: number
  feature_kind: "place" | "trail"
  name: string
  summary?: string
  category?: string
  category_label?: string
  path: string
  photo_url?: string | null
  photo_alt?: string | null
  key_facts?: string
}

export function MapCanvas({
  maptilerKey,
  center,
  zoom = 12,
  marker = false,
  line,
  points,
  dataUrl,
  onPointChange,
  onFeatureSelect,
  className,
}: {
  maptilerKey: string | null
  center: Coordinates
  zoom?: number
  marker?: boolean
  line?: Coordinates[]
  points?: Coordinates[]
  dataUrl?: string
  onPointChange?: (coordinates: Coordinates) => void
  onFeatureSelect?: (feature: MapFeatureProperties) => void
  className?: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const callbackRef = useRef(onPointChange)
  callbackRef.current = onPointChange
  const featureCallbackRef = useRef(onFeatureSelect)
  featureCallbackRef.current = onFeatureSelect

  useEffect(() => {
    if (!maptilerKey || !containerRef.current || typeof window === "undefined") return

    let disposed = false
    let cleanup = () => {}

    void import("maplibre-gl").then((maplibre) => {
      if (disposed || !containerRef.current) return

      const map = new maplibre.Map({
        container: containerRef.current,
        style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${encodeURIComponent(maptilerKey)}`,
        center,
        zoom,
      })
      map.addControl(new maplibre.NavigationControl({ showCompass: false }), "top-right")

      const pointMarker = marker
        ? new maplibre.Marker({ color: "#0E3A54" }).setLngLat(center).addTo(map)
        : null

      const updateFeatures = async () => {
        if (!dataUrl) return
        const bounds = map.getBounds()
        const url = new URL(dataUrl, window.location.origin)
        url.searchParams.set("bbox", [ bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth() ].join(","))
        const response = await fetch(url)
        if (!response.ok) return
        const collection = await response.json()
        const source = map.getSource("places") as GeoJSONSource | undefined
        source?.setData(collection)
      }

      map.on("load", () => {
        if (line && line.length > 1) {
          map.addSource("route", {
            type: "geojson",
            data: { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: line } },
          })
          map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            paint: { "line-color": "#0E3A54", "line-width": 5, "line-opacity": 0.9 },
          })
          const bounds = line.reduce(
            (result, coordinates) => result.extend(coordinates),
            new maplibre.LngLatBounds(line[0], line[0]),
          )
          map.fitBounds(bounds, { padding: 36, maxZoom: 15 })
        }
        if (points && points.length > 0) {
          map.addSource("stops", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: points.map((coordinates, index) => ({ type: "Feature", properties: { order: index + 1 }, geometry: { type: "Point", coordinates } })),
            },
          })
          map.addLayer({
            id: "stops",
            type: "circle",
            source: "stops",
            paint: { "circle-radius": 8, "circle-color": "#147D91", "circle-stroke-color": "#FDFCF9", "circle-stroke-width": 3 },
          })
          const bounds = points.reduce((result, coordinates) => result.extend(coordinates), new maplibre.LngLatBounds(points[0], points[0]))
          map.fitBounds(bounds, { padding: 48, maxZoom: 13 })
        }
        if (dataUrl) {
          map.addSource("places", { type: "geojson", data: { type: "FeatureCollection", features: [] } })
          map.addLayer({
            id: "explore-trails",
            type: "line",
            source: "places",
            filter: [ "==", [ "geometry-type" ], "LineString" ],
            paint: { "line-color": "#D9743A", "line-width": 4, "line-opacity": 0.9 },
          })
          map.addLayer({
            id: "places",
            type: "circle",
            source: "places",
            filter: [ "==", [ "geometry-type" ], "Point" ],
            paint: {
              "circle-radius": 7,
              "circle-color": "#147D91",
              "circle-stroke-color": "#FDFCF9",
              "circle-stroke-width": 2,
            },
          })
          map.on("click", "places", (event) => {
            const properties = event.features?.[0]?.properties as MapFeatureProperties | undefined
            if (properties && featureCallbackRef.current) featureCallbackRef.current(properties)
          })
          map.on("mouseenter", "places", () => { map.getCanvas().style.cursor = "pointer" })
          map.on("mouseleave", "places", () => { map.getCanvas().style.cursor = "" })
          void updateFeatures()
        }
      })
      map.on("moveend", updateFeatures)
      map.on("click", (event) => {
        if (!callbackRef.current) return
        const next: Coordinates = [ event.lngLat.lng, event.lngLat.lat ]
        pointMarker?.setLngLat(next)
        callbackRef.current(next)
      })

      cleanup = () => map.remove()
    })

    return () => {
      disposed = true
      cleanup()
    }
  }, [ maptilerKey, center[0], center[1], zoom, marker, dataUrl, JSON.stringify(line), JSON.stringify(points) ])

  if (!maptilerKey) {
    return (
      <div className={cn("flex items-center justify-center rounded-xl border border-dashed border-hairline bg-surface-subtle p-6 text-center text-ink-muted", className)}>
        <span><MapPinned className="mx-auto h-6 w-6" aria-hidden /><span className="mt-2 block text-sm">Map unavailable until MAPTILER_KEY is configured.</span></span>
      </div>
    )
  }

  return (
    <div className={cn("relative overflow-hidden rounded-xl border border-hairline", className)}>
      <div ref={containerRef} className="h-full w-full" />
      <a
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
        className="absolute right-1 bottom-1 z-10 rounded bg-surface/90 px-1.5 py-0.5 text-[0.62rem] text-ink-muted no-underline shadow-sm hover:text-ink-display"
      >
        © OpenStreetMap contributors · ODbL
      </a>
    </div>
  )
}
